import { useFlag, useFlagsStatus } from "@unleash/proxy-client-react";
import {
  CONSOLIDATION_RENDERING_FEATURE_FLAG,
  RETSINFORMATION_FEATURE_FLAG,
  isConsolidationRenderingEnabled,
  isConsolidationRenderingOverrideActive,
} from "@/config/consolidationFeatureFlags";
import useUnleashContextStore from "@/stores/unleash-context.store";

export type ConsolidationRenderingState = {
  /** Whether consolidation metadata-driven UI is active for this user. */
  enabled: boolean;
  /**
   * Whether the Unleash client has delivered flag values for the signed-in
   * user's context. `enabled` is false until then, so one-shot decisions
   * (e.g. the PDF fallback redirect) must wait for readiness instead of
   * acting on a transient false. `flagsReady` alone is not enough: it flips
   * true after the initial anonymous fetch, before AppRouter has applied
   * userId + tenantId, when segment-constrained flags still evaluate false.
   */
  isReady: boolean;
};

/**
 * Per-user consolidation gate evaluated via the Unleash frontend proxy, like
 * the `Kilde-*` source flags — works for every role, honors the per-user and
 * per-group assignments the Features admin page writes as strategy
 * constraints (LAR-374).
 */
const useConsolidationRenderingEnabled = (): ConsolidationRenderingState => {
  const hasConsolidation = useFlag(CONSOLIDATION_RENDERING_FEATURE_FLAG);
  const hasRetsinformation = useFlag(RETSINFORMATION_FEATURE_FLAG);
  const { flagsReady } = useFlagsStatus();
  const isUserContextSettled = useUnleashContextStore(
    (state) => state.isUserContextSettled
  );

  return {
    enabled: isConsolidationRenderingEnabled({
      hasConsolidation,
      hasRetsinformation,
    }),
    isReady:
      isConsolidationRenderingOverrideActive() ||
      (Boolean(flagsReady) && isUserContextSettled),
  };
};

export default useConsolidationRenderingEnabled;
